import { ForgeIcon, ForgeOption, ForgeSelect, ForgeTextField, ForgeTimePicker } from '@tylertech/forge-react';
import {
  DatesInfo,
  FrequencyConfig,
  TimeInfo,
  TimezoneTypes,
} from 'common/types/frequencySelector/frequencySelector';
import React, { ChangeEvent, Dispatch, FC, SetStateAction, useState } from 'react';
import { timezoneOptions } from '../constants/options';
import { I18n } from 'common/i18n';
import { isDateValidInTimezone } from '../helpers/timezoneHelper';
import { handleTimeAndDatePickerChange } from '../helpers/dateHelper';

const scope = 'shared.components.frequency_selector.time';
const scheduledSendModalScope = 'shared.components.schedule_send_report.modal';

export interface TimeDetailsProps {
  value: TimeInfo;
  datesInfo: DatesInfo;
  setValue: Dispatch<SetStateAction<FrequencyConfig>>;
  isFrequencyTimeInvalid: boolean;
}

const TimeDetails: FC<TimeDetailsProps> = (props) => {
  const { value, setValue, datesInfo, isFrequencyTimeInvalid } = props;
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const onValueChange = <K extends keyof TimeInfo>(field: K, val: TimeInfo[K]) => {
    //validate start date when timezone value is changed
    if (field === 'timeZone' && datesInfo.startDate) {
      const isStartDateValid = isDateValidInTimezone(datesInfo.startDate, val as TimezoneTypes);

      if (!isStartDateValid) {
        setValue((prevState) => ({
          ...prevState,
          datesInfo: {
            ...prevState.datesInfo,
            startDate: undefined,
            endDate: undefined,
          },
        }));
      }
    }

    setValue((prevState) => ({
      ...prevState,
      timeInfo: {
        ...prevState.timeInfo,
        [field]: val,
      },
    }));
  };

  const isTimeInvalid = !value.time && isFrequencyTimeInvalid;

  /*
    We will remove the 'value' property from the ForgeTimePicker if we are currently updating the field.
    As per checking, the time picker has some autocomplete behavior if the 'value' property gets updated
   */
  const valueProp = !isUpdating ? { value: value.time } : {};

  const handleForgeTimePickerChange = (event: CustomEvent) => {
    handleTimeAndDatePickerChange(isUpdating, setIsUpdating, () => onValueChange('time', event.detail));
  };

  return (
    <div className="picker-sub-container time" data-testid="frequency-selector-time">
      <p className="forge-typography--body2">{I18n.t('time_label', { scope })}</p>
      <ForgeTimePicker
        step={30}
        on-forge-time-picker-change={handleForgeTimePickerChange}
        allowInput={true}
        className="duration-time"
        data-testid="time-picker"
        {...valueProp}
      >
        <ForgeTextField required={isTimeInvalid} invalid={isTimeInvalid}>
          <input
            type="text"
            id="time-picker"
            data-testid="time-picker-input-field"
            onBlur={() => setIsUpdating(false)}
          />
          {!value.time && <label htmlFor="time-picker">{I18n.t('time_field_placeholder', { scope })}</label>}
          {isTimeInvalid && (
            <span slot="support-text">{I18n.t('required_field', { scope: scheduledSendModalScope })}</span>
          )}
        </ForgeTextField>
      </ForgeTimePicker>
      <ForgeSelect
        className="duration-time-zone"
        data-testid="timezone-select"
        value={value.timeZone}
        variant="plain"
        theme="primary"
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onValueChange('timeZone', event.target.value as TimezoneTypes)
        }
        popoverIcon={false}
      >
        {timezoneOptions.map((option) => (
          <ForgeOption key={`timezone_${option.value}`} value={option.value}>
            {option.label}
          </ForgeOption>
        ))}
        <ForgeIcon slot="end" name="chevron_down" icon_type="button" theme="primary" />
      </ForgeSelect>
    </div>
  );
};

export default TimeDetails;
